import type {
  AppSettings,
  Category,
  DailySummary,
  DiagnosticsResult,
  HeldOrder,
  MultiStoreDailySummary,
  MultiStoreSalesTrend,
  OfficialReceipt,
  Order,
  ParsedOrderResult,
  PaymentMethod,
  Product,
  SalesTrendPoint,
  Staff,
  StaffEmploymentType,
  StaffRole,
  Store,
  Table,
} from "@ai-pos/shared";
import { apiClient } from "./client.js";

export type { Category };

export interface AuthContextResponse {
  staff: Staff;
  activeStoreId: string;
  storeName: string;
  stores: Store[];
}

export const authApi = {
  login: (pin: string) =>
    apiClient.post<AuthContextResponse>("/auth/login", { pin }),
  logout: () => apiClient.post<{ ok: boolean }>("/auth/logout"),
  me: () => apiClient.get<AuthContextResponse>("/auth/me"),
  myStores: () => apiClient.get<{ stores: Store[] }>("/auth/my-stores"),
  switchStore: (storeId: string) =>
    apiClient.post<{ activeStoreId: string; storeName: string }>(
      "/auth/switch-store",
      { storeId },
    ),
  changePin: (currentPin: string, newPin: string) =>
    apiClient.post<{ ok: boolean }>("/auth/change-pin", { currentPin, newPin }),
};

export const storesApi = {
  list: () => apiClient.get<{ stores: Store[] }>("/stores"),
  create: (input: {
    name: string;
    address: string;
    phone: string;
    registrationNumber: string;
    isActive: boolean;
  }) => apiClient.post<{ store: Store }>("/stores", input),
  update: (
    id: string,
    input: Partial<{
      name: string;
      address: string;
      phone: string;
      registrationNumber: string;
      isActive: boolean;
    }>,
  ) => apiClient.put<{ store: Store }>(`/stores/${id}`, input),
};

export const productsApi = {
  list: (includeInactive = false) =>
    apiClient.get<{ products: Product[] }>(
      `/products${includeInactive ? "?includeInactive=true" : ""}`,
    ),
  create: (
    input: Omit<
      Product,
      "id" | "createdAt" | "updatedAt" | "categoryName" | "priceIncludingTax"
    >,
  ) => apiClient.post<{ product: Product }>("/products", input),
  update: (
    id: string,
    input: Omit<
      Product,
      "id" | "createdAt" | "updatedAt" | "categoryName" | "priceIncludingTax"
    >,
  ) => apiClient.put<{ product: Product }>(`/products/${id}`, input),
  remove: (id: string) => apiClient.delete<{ ok: boolean }>(`/products/${id}`),
};

export const categoriesApi = {
  list: () => apiClient.get<{ categories: Category[] }>("/categories"),
  create: (input: {
    name: string;
    sortOrder: number;
    isActive: boolean;
    parentCategoryId: string | null;
  }) => apiClient.post<{ category: Category }>("/categories", input),
  update: (
    id: string,
    input: Partial<{
      name: string;
      sortOrder: number;
      isActive: boolean;
      parentCategoryId: string | null;
    }>,
  ) => apiClient.put<{ category: Category }>(`/categories/${id}`, input),
  copyToStores: (id: string, storeIds: string[]) =>
    apiClient.post<{ results: Array<{ storeId: string; ok: boolean }> }>(
      `/categories/${id}/copy-to-stores`,
      {
        storeIds,
      },
    ),
  remove: (id: string) =>
    apiClient.delete<{ ok: boolean }>(`/categories/${id}`),
};

export const paymentMethodsApi = {
  list: () =>
    apiClient.get<{ paymentMethods: PaymentMethod[] }>("/payment-methods"),
  create: (input: {
    name: string;
    code: string;
    isActive: boolean;
    sortOrder: number;
  }) =>
    apiClient.post<{ paymentMethod: PaymentMethod }>("/payment-methods", input),
  update: (
    id: string,
    input: Partial<{
      name: string;
      code: string;
      isActive: boolean;
      sortOrder: number;
    }>,
  ) =>
    apiClient.put<{ paymentMethod: PaymentMethod }>(
      `/payment-methods/${id}`,
      input,
    ),
};

export const tablesApi = {
  list: () => apiClient.get<{ tables: Table[] }>("/tables"),
  create: (input: { name: string; isActive: boolean; sortOrder: number }) =>
    apiClient.post<{ table: Table }>("/tables", input),
  update: (
    id: string,
    input: Partial<{ name: string; isActive: boolean; sortOrder: number }>,
  ) => apiClient.put<{ table: Table }>(`/tables/${id}`, input),
  remove: (id: string) => apiClient.delete<{ ok: boolean }>(`/tables/${id}`),
};

export const staffApi = {
  list: () => apiClient.get<{ staff: Staff[] }>("/staff"),
  create: (input: {
    name: string;
    pin: string;
    role: StaffRole;
    employmentType: StaffEmploymentType;
    storeId: string;
  }) => apiClient.post<{ staff: Staff }>("/staff", input),
  update: (
    id: string,
    input: Partial<{
      name: string;
      role: StaffRole;
      employmentType: StaffEmploymentType;
      isActive: boolean;
      pin: string;
    }>,
  ) => apiClient.put<{ staff: Staff }>(`/staff/${id}`, input),
  addStoreAccess: (id: string, storeId: string) =>
    apiClient.post<{ staff: Staff }>(`/staff/${id}/stores`, { storeId }),
  removeStoreAccess: (id: string, storeId: string) =>
    apiClient.delete<{ staff: Staff }>(`/staff/${id}/stores/${storeId}`),
};

export const settingsApi = {
  get: () => apiClient.get<{ settings: AppSettings }>("/settings"),
  getPublic: () =>
    apiClient.get<{
      storeName: string;
      autoLockMinutes: number;
      officialReceiptDefaultDescription: string;
    }>("/settings/public"),
  update: (patch: Partial<AppSettings>) =>
    apiClient.put<{ settings: AppSettings }>("/settings", patch),
  regenerateBridgeToken: () =>
    apiClient.post<{ printerBridgeToken: string }>(
      "/settings/printer-bridge-token/regenerate",
    ),
};

export interface CheckoutInput {
  idempotencyKey: string;
  items: Array<{
    productId: string;
    quantity: number;
    note?: string;
    paymentMethodId: string;
    unitPrice?: number;
  }>;
  discount: number;
  guestCount: number;
  note?: string;
}

export interface CheckoutResponse {
  order: Order;
  printResult: { attempted: boolean; success: boolean; message: string };
  isDuplicate: boolean;
}

export const ordersApi = {
  checkout: (input: CheckoutInput) =>
    apiClient.post<CheckoutResponse>("/orders", input),
  list: (query: Record<string, string | undefined>) => {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) {
      if (value) params.set(key, value);
    }
    const qs = params.toString();
    return apiClient.get<{ orders: Order[] }>(`/orders${qs ? `?${qs}` : ""}`);
  },
  get: (id: string) => apiClient.get<{ order: Order }>(`/orders/${id}`),
  print: (id: string) =>
    apiClient.post<{ printResult: { success: boolean; message: string } }>(
      `/orders/${id}/print`,
    ),
  reprint: (id: string) =>
    apiClient.post<{ printResult: { success: boolean; message: string } }>(
      `/orders/${id}/reprint`,
    ),
  void: (id: string, reason: string) =>
    apiClient.post<{ order: Order }>(`/orders/${id}/void`, { reason }),
  draftPrint: (input: {
    items: Array<{
      productId: string;
      quantity: number;
      note?: string;
      paymentMethodId: string;
      unitPrice?: number;
    }>;
    discount: number;
    guestCount: number;
  }) =>
    apiClient.post<{ printResult: { success: boolean; message: string } }>(
      "/orders/draft-print",
      input,
    ),
};

export const receiptsApi = {
  issue: (
    orderId: string,
    input: { recipientName: string; description: string },
  ) =>
    apiClient.post<{
      receipt: OfficialReceipt;
      printResult: { success: boolean; message: string };
    }>(`/orders/${orderId}/receipt`, input),
  list: (orderId: string) =>
    apiClient.get<{ receipts: OfficialReceipt[] }>(
      `/orders/${orderId}/receipts`,
    ),
};

export const heldOrdersApi = {
  list: () => apiClient.get<{ heldOrders: HeldOrder[] }>("/held-orders"),
  create: (input: {
    guestCount: number;
    items: HeldOrder["items"];
    tableId: string;
    note?: string;
  }) => apiClient.post<{ heldOrder: HeldOrder }>("/held-orders", input),
  remove: (id: string) =>
    apiClient.delete<{ ok: boolean }>(`/held-orders/${id}`),
};

export const dailySummaryApi = {
  get: (businessDate?: string) =>
    apiClient.get<{ summary: DailySummary }>(
      `/daily-summary${businessDate ? `?businessDate=${businessDate}` : ""}`,
    ),
  getMultiStore: (businessDate?: string) =>
    apiClient.get<{ summary: MultiStoreDailySummary }>(
      `/daily-summary/multi-store${businessDate ? `?businessDate=${businessDate}` : ""}`,
    ),
  getTrend: (startDate: string, endDate: string) =>
    apiClient.get<{ points: SalesTrendPoint[] }>(
      `/daily-summary/trend?startDate=${startDate}&endDate=${endDate}`,
    ),
  getMultiStoreTrend: (startDate: string, endDate: string) =>
    apiClient.get<{ trend: MultiStoreSalesTrend }>(
      `/daily-summary/multi-store/trend?startDate=${startDate}&endDate=${endDate}`,
    ),
  print: (businessDate?: string) =>
    apiClient.post<{ printResult: { success: boolean; message: string } }>(
      `/daily-summary/print${businessDate ? `?businessDate=${businessDate}` : ""}`,
    ),
};

export const printerApi = {
  testPrint: () =>
    apiClient.post<{ result: { success: boolean; message: string } }>(
      "/printer/test-print",
    ),
  checkConnection: () =>
    apiClient.post<{ result: { ok: boolean; message: string } }>(
      "/printer/check-connection",
    ),
  printJobs: (orderId?: string) =>
    apiClient.get<{
      printJobs: Array<{
        id: string;
        orderId: string;
        receiptNumber: string;
        provider: string;
        status: "SUCCESS" | "FAILED";
        isReprint: boolean;
        errorMessage?: string;
        previewUrl?: string;
        createdAt: string;
      }>;
    }>(`/print-jobs${orderId ? `?orderId=${orderId}` : ""}`),
};

export const orderParserApi = {
  parse: (text: string) =>
    apiClient.post<{ result: ParsedOrderResult; provider: string }>(
      "/order-parser/parse",
      { text },
    ),
};

export const diagnosticsApi = {
  get: () => apiClient.get<{ diagnostics: DiagnosticsResult }>("/diagnostics"),
};
